import { ShapeControlButton } from './controls/ShapeControlButton';
import {
  ArrowUp,
  ArrowLeft,
  ArrowDown,
  ArrowRight,
  AlignHorizontalSpaceAround
} from 'lucide-react';
import React from 'react';
import { Shape, SpotOrientation, SpotShape } from '../../../../../types';
import { useMapEditor } from '../../../contexts/MapEditorContext';
import { TooltipProvider } from '../../../../ui/tooltip';
import { getAbsolutePositionOfShape } from '../../../helpers/getAbsolutePositionOfShape';
import { getWindowKonvaObject } from '../../../helpers/konva/getWindowKonvaObject';
import { getElementSize } from '../../../helpers/getElementSize';
import { useMapCanvas } from '../../../contexts/MapCanvasContext';
import { generateNextSpotLabels } from '../../../helpers/generateNextSpotLabels';
import { Popover, PopoverContent, PopoverTrigger } from '@gettactic/components';
import { DragNumberInput } from '../../controls/ScrollDragNumberInput';

export interface ResourceToolBox {
  shapes: Shape[];
}

const VERTICAL_MARGIN = -18;
const HORIZONTAL_MARGIN = -8;

export const ResourceToolBox: React.FC<ResourceToolBox> = ({ shapes }) => {
  const { duplicateShape, updateShapes, updateShape } = useMapEditor();
  const { lastSpotLabel, spotLabelPattern, setLastSpotLabel } = useMapCanvas();

  const [preserveSpacing, setPreserveSpacing] = React.useState(false);
  const [horizontalSpacing, setHorizontalSpacing] = React.useState(0);
  const [verticalSpacing, setVerticalSpacing] = React.useState(0);

  const stage = getWindowKonvaObject().stages[0];

  const absPos = (shapes[0]
    ? getAbsolutePositionOfShape(shapes[0].id, stage)
    : undefined) || {
    x: 0,
    y: 0,
    xEnd: 0,
    yEnd: 0
  };

  const sizes = React.useMemo(() => {
    return shapes.map((shape) => getElementSize(shape.id, stage));
  }, [shapes]);

  const duplicateShapes = (direction: 'right' | 'bottom' | 'left' | 'top') => {
    if (!sizes.length) return;

    const margin =
      direction === 'right' || direction === 'left'
        ? HORIZONTAL_MARGIN
        : VERTICAL_MARGIN;

    const minX = Math.min(...sizes.map((size) => size?.x || 0));
    const minY = Math.min(...sizes.map((size) => size?.y || 0));
    const maxX = Math.max(
      ...sizes.map((size) => (size?.x || 0) + (size?.width || 0))
    );
    const maxY = Math.max(
      ...sizes.map((size) => (size?.y || 0) + (size?.height || 0))
    );

    const groupWidth = maxX - minX;
    const groupHeight = maxY - minY;

    const override: (Partial<Shape> & Required<Pick<Shape, 'id'>>)[] = [];
    const duplicateIds = shapes.map((shape) => shape.id);

    let lastSpotNumber: string[] | null = lastSpotLabel || [];

    shapes.forEach((shape, index) => {
      const size = sizes[index];
      if (!size) return;

      const offsetX = (size.x || 0) - minX;
      const offsetY = (size.y || 0) - minY;

      let x = minX + offsetX;
      let y = minY + offsetY;
      let orientation: SpotOrientation | undefined = undefined;

      if (direction === 'right') {
        x = minX + groupWidth + margin + offsetX;
      } else if (direction === 'left') {
        x = minX - margin - groupWidth + offsetX;
      } else if (direction === 'bottom') {
        y = minY + groupHeight + margin + offsetY;
      } else if (direction === 'top') {
        y = minY - margin - groupHeight + offsetY;
      }

      if (shape.type === 'spot') {
        const spotShape = shape as SpotShape;
        orientation = spotShape.orientation;

        if (direction === 'right' || direction === 'left') {
          if (orientation === 'left') orientation = 'right';
          else if (orientation === 'right') orientation = 'left';
        } else if (direction === 'top' || direction === 'bottom') {
          if (orientation === 'top') orientation = 'bottom';
          else if (orientation === 'bottom') {
            orientation = 'top';
            y += 20;
          }
        }
      }

      if (direction === 'right' || direction === 'bottom') {
        lastSpotNumber = generateNextSpotLabels(
          spotLabelPattern,
          lastSpotNumber || []
        );
      }

      duplicateIds.push(shape.id);
      override.push({
        id: shape.id,
        x,
        y,
        orientation,
        ...(direction === 'right' || direction === 'bottom'
          ? { label: lastSpotNumber?.join('') || '' }
          : {})
      });
    });

    duplicateShape(duplicateIds, {
      override,
      selectDuplicates: true
    });

    if (direction === 'right' || direction === 'bottom') {
      setLastSpotLabel(lastSpotNumber);
    }
  };

  const _duplicateShapesHorizontally = () => {
    duplicateShapes('right');
  };

  const _duplicateShapesVertically = () => {
    duplicateShapes('bottom');
  };

  const _duplicateShapesToLeft = () => {
    duplicateShapes('left');
  };

  const _duplicateShapesToTop = () => {
    duplicateShapes('top');
  };

  const _moveShapes = ({
    preserveSpacing,
    horizontalSpacing,
    verticalSpacing
  }: {
    preserveSpacing: boolean;
    horizontalSpacing: number;
    verticalSpacing: number;
  }) => {
    if (!shapes.length) return;

    function groupBy<T>(
      list: T[],
      keyGetter: (item: T) => string
    ): Map<string, T[]> {
      const map = new Map<string, T[]>();
      list.forEach((item) => {
        const key = keyGetter(item);
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key)!.push(item);
      });
      return map;
    }

    const TOLERANCE = 10; // Tolerance for grouping rows/columns

    // ----- Horizontal Spacing -----
    if (horizontalSpacing >= 0) {
      const horizontalOrientations = ['left', 'right', 'top', 'bottom'];

      for (const orientation of horizontalOrientations) {
        const shapesByOrientation = shapes.filter(
          (s) => (s as SpotShape).orientation === orientation
        );

        // Group shapes by approximate Y (row)
        const groups = groupBy(
          shapesByOrientation,
          (s) => `${Math.round((s.y || 0) / TOLERANCE) * TOLERANCE}`
        );

        for (const group of groups.values()) {
          const sorted = [...group].sort((a, b) => (a.x || 0) - (b.x || 0));
          let currentX = sorted[0]?.x || 0;

          for (let i = 0; i < sorted.length; i++) {
            const shape = sorted[i];
            const size = getElementSize(shape.id, stage);
            if (!size) continue;

            updateShape(shape.id, { x: currentX });

            currentX += size.width - 10 + horizontalSpacing;
          }
        }
      }
    }

    // ----- Vertical Spacing -----
    if (verticalSpacing >= 0) {
      const verticalOrientations = ['left', 'right', 'top', 'bottom'];

      for (const orientation of verticalOrientations) {
        const shapesByOrientation = shapes.filter(
          (s) => (s as SpotShape).orientation === orientation
        );

        // Group shapes by approximate X (column)
        const groups = groupBy(
          shapesByOrientation,
          (s) => `${Math.round((s.x || 0) / TOLERANCE) * TOLERANCE}`
        );

        for (const group of groups.values()) {
          const sorted = [...group].sort((a, b) => (a.y || 0) - (b.y || 0));
          let currentY = sorted[0]?.y || 0;

          for (let i = 0; i < sorted.length; i++) {
            const shape = sorted[i];
            const size = getElementSize(shape.id, stage);
            if (!size) continue;

            updateShape(shape.id, { y: currentY });

            currentY += size.height + verticalSpacing;
          }
        }
      }
    }
  };

  if (!stage || !shapes.length) return null;

  return (
    <TooltipProvider>
      <div
        style={{
          left: (absPos.x + absPos.xEnd) / 2,
          top: absPos.yEnd + 10,
          transform: 'translateX(-50%)'
        }}
        className={
          'resource-tool-box -translate-x-1/2 fade-in-0 zoom-in-95 fixed z-sticky flex animate-in items-center gap-1 rounded-lg border bg-background-primary p-1 shadow-lg'
        }
      >
        <ShapeControlButton
          className="text-blue-500"
          onClick={() => _duplicateShapesHorizontally()}
          title={'Add Row (Right)'}
          icon={ArrowRight}
        />
        <ShapeControlButton
          className="text-blue-500"
          onClick={() => _duplicateShapesVertically()}
          title={'Add Column (Bottom)'}
          icon={ArrowDown}
        />
        <ShapeControlButton
          className="text-blue-500"
          onClick={() => _duplicateShapesToTop()}
          title={'Add Column (Top)'}
          icon={ArrowUp}
        />
        <ShapeControlButton
          className="text-blue-500"
          onClick={() => _duplicateShapesToLeft()}
          title={'Add Row (Left)'}
          icon={ArrowLeft}
        />
        {shapes.length > 1 && (
          <>
            <div className="h-4 w-px bg-gray-200" />

            <Popover>
              <PopoverTrigger>
                <ShapeControlButton
                  className="text-green-500"
                  onClick={() => {}}
                  title={'Move with spacing'}
                  icon={AlignHorizontalSpaceAround}
                />
              </PopoverTrigger>
              <PopoverContent className="w-64">
                <div className="flex flex-col gap-4">
                  {/*<div>
                    <label className="mb-2 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={preserveSpacing}
                        onChange={(e) => {
                          setPreserveSpacing(e.target.checked);
                          _moveShapes({
                            preserveSpacing: e.target.checked,
                            horizontalSpacing,
                            verticalSpacing
                          });
                        }}
                      />
                      <span className="font-medium text-sm">
                        Preserve Spacing
                      </span>
                    </label>
                  </div>*/}

                  <div>
                    <div className="mb-2 font-medium text-sm">
                      Horizontal spacing
                    </div>
                    <DragNumberInput
                      size="sm"
                      value={horizontalSpacing || 0}
                      onChange={(value) => {
                        setHorizontalSpacing(value);
                        _moveShapes({
                          preserveSpacing,
                          horizontalSpacing: value,
                          verticalSpacing
                        });
                      }}
                      min={0}
                      max={200}
                    />
                  </div>

                  <div>
                    <div className="mb-2 font-medium text-sm">
                      Vertical spacing
                    </div>
                    <DragNumberInput
                      size="sm"
                      value={verticalSpacing || 0}
                      onChange={(value) => {
                        setVerticalSpacing(value);
                        _moveShapes({
                          preserveSpacing,
                          horizontalSpacing,
                          verticalSpacing: value
                        });
                      }}
                      min={0}
                      max={200}
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}
      </div>
    </TooltipProvider>
  );
};
